import { AlertCircle, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { userService } from '../../../services/user.service';

import UserTable from '../components/UserTable';
import SearchFilters from '../components/SearchFilters';
import CreateUserModal from '../components/modals/CreateUserModal';
import EditUserModal from '../components/modals/EditUserModal';
import StatusChangeModal from '../components/modals/StatusChangeModal';

const UserManagementList = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [success, setSuccess] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [limit] = useState(10); // Users per page
  const [exportLoading, setExportLoading] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await userService.getUsers({
        role: roleFilter !== 'all' ? roleFilter : undefined,
        search: searchQuery || undefined,
        page: currentPage,
        limit
      });

      if (result?.status === 'success' && Array.isArray(result.data)) {
        setUsers(result.data);
        setTotalUsers(result.pagination.total);
        setTotalPages(Math.ceil(result.pagination.total / result.pagination.limit));
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
    fetchUsers();
  }, [roleFilter, searchQuery]);

  useEffect(() => {
    fetchUsers();
  }, [currentPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleEditClick = (user) => {
    if (!user?._id) return;
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleStatusClick = (user) => {
    if (!user?._id) return;
    setSelectedUser(user);
    setShowStatusModal(true);
  };

  const showSuccessMessage = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleCreateSuccess = async (newUser) => {
    try {
      setShowCreateModal(false);
      showSuccessMessage('User created successfully');
      await fetchUsers();  // Refresh the list
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create user');
    }
  };

  const handleEditSuccess = async (userId, userData) => {
    try {
      setLoading(true);
      setError(null);
      await userService.updateUser(userId, userData);
      setShowEditModal(false);
      setSelectedUser(null);
      showSuccessMessage('User updated successfully');
      await fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChangeSuccess = async (userId, isActive) => {
    try {
      setLoading(true);
      setError(null);
      await userService.updateUserStatus(userId, isActive);
      setShowStatusModal(false);
      setSelectedUser(null);
      showSuccessMessage(`User ${isActive ? 'activated' : 'deactivated'} successfully`);
      await fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user status');
    } finally {
      setLoading(false);
    }
  };

  const handleExportUsers = async () => {
    try {
      setExportLoading(true);
      const response = await userService.exportUsers({
        role: roleFilter !== 'all' ? roleFilter : undefined,
        search: searchQuery || undefined
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users-export-${new Date().toISOString()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSuccessMessage('Users exported successfully');
    } catch (err) {
      setError('Failed to export users');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Management</h1>
          <div className="flex items-center space-x-3">
            {(currentUser.role === 'admin' || currentUser.role === 'receptionist') && (
              <>
                <button
                  onClick={handleExportUsers}
                  disabled={exportLoading}
                  className="px-4 py-2 text-blue-600 dark:text-blue-400 border border-blue-600 dark:border-blue-400 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30 flex items-center transition-colors"
                >
                  {exportLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 dark:border-blue-400 mr-2" />
                  ) : null}
                  Export Users
                </button>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-500 flex items-center transition-colors"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Create User
                </button>
              </>
            )}
          </div>
        </div>

        {/* Search and Filters */}
        <SearchFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          roleFilter={roleFilter}
          onRoleFilterChange={setRoleFilter}
          loading={loading}
        />
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-400 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="ml-3 text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 dark:bg-green-900/10 border-l-4 border-green-400 p-4">
          <p className="text-sm text-green-700 dark:text-green-300">{success}</p>
        </div>
      )}

      {/* User Table */}
      <UserTable
        currentUser={currentUser.role}
        users={users}
        onEditClick={handleEditClick}
        onStatusClick={handleStatusClick}
        loading={loading}
      />

      {/* Pagination Controls */}
      {!loading && totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between bg-white dark:bg-slate-800 px-4 py-3 rounded-lg shadow sm:px-6 border border-gray-200 dark:border-gray-700 transition-colors">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Showing{' '}
                <span className="font-medium">
                  {((currentPage - 1) * limit) + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * limit, totalUsers)}
                </span>{' '}
                of{' '}
                <span className="font-medium">{totalUsers}</span>{' '}
                results
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-slate-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50 transition-colors"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeft className="h-5 w-5" />
                </button>

                {/* Page Numbers */}
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  const isCurrentPage = pageNumber === currentPage;

                  // Show first page, last page, and pages around current page
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold transition-colors
                          ${isCurrentPage
                            ? 'z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                            : 'text-gray-900 dark:text-gray-300 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-slate-700 focus:outline-offset-0'
                          }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  }

                  // Show ellipsis
                  if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return (
                      <span
                        key={pageNumber}
                        className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 ring-1 ring-inset ring-gray-300 dark:ring-gray-600"
                      >
                        ...
                      </span>
                    );
                  }

                  return null;
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-slate-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50 transition-colors"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRight className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateUserModal
          creatorRole={currentUser.role}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {showEditModal && selectedUser && (
        <EditUserModal
          user={selectedUser}
          editorRole={currentUser.user}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSuccess={handleEditSuccess}
        />
      )}

      {showStatusModal && selectedUser && (
        <StatusChangeModal
          user={selectedUser}
          onClose={() => {
            setShowStatusModal(false);
            setSelectedUser(null);
          }}
          onSuccess={handleStatusChangeSuccess}
        />
      )}
    </div>
  );
};

export default UserManagementList;